import { useMutation } from '@apollo/client';
import {
  Arrange,
  Container,
  Icon,
  Spacer,
  SvgComment,
  SvgEye,
  SvgSmile,
  Text,
} from '@loomhq/lens';
import moment from 'moment';
import React, { MutableRefObject } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { showErrorBar } from 'actions/errorBar';
import { discardRenameCard } from 'actions/videosDashboard';
import { ERROR } from 'constants/error-severities';
import { ContextType } from 'utilities/menuOptions/types';
import { shorthandTimestampMomentConfig } from 'utilities/time';

import UpdateVideoName from '../../creators/UpdateVideoName.graphql';
import { VideoCardVideo } from './types';
import { VideoVisibility } from './VideoVisibility';
import { useLogTagClickedMutation } from '../tags/tag-events';
import { PROFILE_CONTEXT } from '../../constants/videoCard';

import UserComponentWithProfileHover from 'components/user-profile/user-component-with-profile-hover';
import CardRenameInput from 'components/videos-dashboard-ta/card-rename-input';

import styles from './styles.module.less';

export default function Body({
  video,
  context,
  linkRef,
  showPostVideoFTUX = false,
  setHasActiveHoverState,
}: {
  video: VideoCardVideo;
  context: ContextType;
  linkRef: MutableRefObject<HTMLAnchorElement | null>;
  showPostVideoFTUX: boolean;
  setHasActiveHoverState?: (hasActiveHoverState: boolean) => void;
}): JSX.Element {
  const dispatch = useDispatch();
  const logTagClicked = useLogTagClickedMutation();

  const renameId = useSelector(
    state => (state as { videos_dashboard }).videos_dashboard.rename_id
  );

  const [renameVideo] = useMutation(UpdateVideoName, {
    onCompleted: ({ result }) => {
      dispatch(discardRenameCard());

      if (result.__typename === 'GenericError') {
        dispatch(showErrorBar(ERROR, result.message));
      }
    },
  });

  const renameVideoTitle = name => {
    renameVideo({
      variables: { id: video.id, name },
    });
  };

  const isProfilePage = context === PROFILE_CONTEXT;
  const isOwnerProfilePage = isProfilePage && video.current_user_is_owner;

  moment.locale('en', shorthandTimestampMomentConfig());

  return (
    <Spacer all="medium">
      <Arrange alignItems="center" gap="small">
        {!isOwnerProfilePage && (
          <UserComponentWithProfileHover
            profileId={video.owner.id}
            hasFocus={false}
            avatarSize={4}
            fontSize={null}
          />
        )}
        <div className="overflow:hidden">
          {!isOwnerProfilePage && (
            <UserComponentWithProfileHover
              profileId={video.owner.id}
              type="name"
              hasFocus={true}
              avatarSize={null}
              fontSize="small"
            />
          )}
          <Text htmlTag="p" color="bodyDimmed" size="small">
            {moment(video?.createdAt).fromNow()}
            {!video.archived && (
              <span className={styles.videoVisibility}>
                ・
                <VideoVisibility
                  video={video}
                  textSize="small"
                  context={context}
                  showPostVideoFTUX={showPostVideoFTUX}
                  setHasActiveHoverState={setHasActiveHoverState}
                />
              </span>
            )}
          </Text>
        </div>
      </Arrange>
      <Spacer top="small" bottom={1.5}>
        <Container height="2.75rem" aria-hidden="true">
          {renameId === video?.id ? (
            <CardRenameInput
              name={video?.name}
              onSubmit={name => renameVideoTitle(name)}
            />
          ) : (
            <Text fontWeight="medium" hasEllipsis ellipsisLines={2}>
              {video?.name}
            </Text>
          )}
        </Container>
      </Spacer>
      <div className={styles.videoCardFooter}>
        <div className={styles.videoCardInsights}>
          <Arrange gap="medium">
            <Arrange gap="xsmall">
              <Icon color="bodyDimmed" icon={<SvgEye />} size={2} />
              <Text color="bodyDimmed" size="small">
                {video?.views?.total || 0}
              </Text>
            </Arrange>
            <Arrange gap="xsmall">
              <Icon color="bodyDimmed" icon={<SvgComment />} size={2} />
              <Text color="bodyDimmed" size="small">
                {video?.totalComments || 0}
              </Text>
            </Arrange>
            <Arrange gap="xsmall">
              <Icon color="bodyDimmed" icon={<SvgSmile />} size={2} />
              <Text color="bodyDimmed" size="small">
                {video?.totalReactions || 0}
              </Text>
            </Arrange>
          </Arrange>
        </div>

        <ScrollContainer
          className={styles.videoCardTags}
          onStartScroll={() => linkRef.current?.classList.add(styles.stayOpen)}
          onEndScroll={() => linkRef.current?.classList.remove(styles.stayOpen)}
        >
          <Arrange gap="xsmall">
            {video?.tags.map(tag => (
              <Link
                key={tag}
                to={`/tag/${tag}`}
                onClick={() => {
                  logTagClicked({
                    variables: {
                      tag,
                      context: 'VideoCard',
                    },
                  });
                }}
              >
                <Text
                  color="primary"
                  size="small"
                  hasEllipsis
                  className={styles.videoCardTagLink}
                >
                  #{tag}
                </Text>
              </Link>
            ))}
          </Arrange>
        </ScrollContainer>
      </div>
    </Spacer>
  );
}
